import { AxiosResponse } from 'axios';
import apiClient from '../client';
import { ApiResponseDto } from 'interfaces/apiResponse';
import {
  SmsState,
  TenantAdminDetail,
  TenantAdminUpdateRequest,
  TenantOnboardRequest,
  TenantOnboardResponse,
  TenantRenewSmsQuotaRequest,
} from 'interfaces/tenant';

interface AdminTenantService {
  list(): Promise<TenantAdminDetail[]>;
  get(alias: string): Promise<TenantAdminDetail>;
  onboard(data: TenantOnboardRequest): Promise<TenantOnboardResponse>;
  update(alias: string, data: TenantAdminUpdateRequest): Promise<TenantAdminDetail>;
  renewQuota(alias: string, data: TenantRenewSmsQuotaRequest): Promise<void>;
  sendTestSms(alias: string, phone: string): Promise<SmsTestSendResult>;
  getLastTestSmsStatus(alias: string): Promise<TestSmsStatusResult | null>;
}

export interface SmsTestSendResult {
  tenantAlias: string;
  organizationId: string;
  senderName: string | null;
  content: string;
  smsAttributesConfigured: boolean;
  bookingNotificationsEnabled: boolean;
}

export interface TestSmsStatusResult {
  smsId: number;
  state: SmsState;
  createdAt: string;
  externalIdentifier: string | null;
}

const basePath = '/admin/tenants';

const unwrap = <T>(response: AxiosResponse<ApiResponseDto<T>>): T => {
  if (response.data.errors) {
    throw new Error(response.data.errors.map(error => error.message).join(', '));
  }
  return response.data.data!;
};

export const adminTenantsService: AdminTenantService = {
  list: async () => unwrap(await apiClient.get<ApiResponseDto<TenantAdminDetail[]>>(basePath)),
  get: async alias => unwrap(await apiClient.get<ApiResponseDto<TenantAdminDetail>>(`${basePath}/${alias}`)),
  onboard: async data => unwrap(await apiClient.post<ApiResponseDto<TenantOnboardResponse>>(basePath, data)),
  update: async (alias, data) => unwrap(await apiClient.put<ApiResponseDto<TenantAdminDetail>>(`${basePath}/${alias}`, data)),
  renewQuota: async (alias, data) => {
    unwrap(await apiClient.post<ApiResponseDto<void>>(`${basePath}/${alias}/sms/renew-quota`, data));
  },
  sendTestSms: async (alias, phone) =>
    unwrap(await apiClient.post<ApiResponseDto<SmsTestSendResult>>(`/admin/diagnostics/booking-reminders/${alias}/test-sms`, { phone })),
  getLastTestSmsStatus: async alias =>
    unwrap(await apiClient.get<ApiResponseDto<TestSmsStatusResult | null>>(`/admin/diagnostics/booking-reminders/${alias}/test-sms`)),
};
